import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, Globe, Palette, Type } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { brandingService, UpdateBrandingDto } from '@/features/branding/services/branding.service';
import { useBrandingStore } from '@/store/branding.store';

export const Route = createFileRoute('/_authenticated/admin/branding')({
  component: BrandingAdminRoute,
});

function BrandingAdminRoute() {
  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <BrandingAdminPage />
    </PermissionGate>
  );
}

function BrandingAdminPage() {
  const queryClient = useQueryClient();
  const setBranding = useBrandingStore((s) => s.setBranding);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const { data: branding, isLoading } = useQuery({
    queryKey: ['branding', 'me'],
    queryFn: () => brandingService.getMyBranding(),
  });

  const [form, setForm] = useState<UpdateBrandingDto>({});

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateBrandingDto) => brandingService.update(dto),
    onSuccess: (data) => {
      setBranding(data);
      queryClient.invalidateQueries({ queryKey: ['branding', 'me'] });
      toast.success('Configurações salvas!');
    },
  });

  const logoMutation = useMutation({
    mutationFn: (file: File) => brandingService.uploadLogo(file),
    onSuccess: (data) => {
      setBranding(data);
      queryClient.invalidateQueries({ queryKey: ['branding', 'me'] });
      toast.success('Logo atualizado!');
    },
    onError: () => toast.error('Erro ao enviar logo. Use PNG, JPG ou SVG (máx 2MB).'),
  });

  const faviconMutation = useMutation({
    mutationFn: (file: File) => brandingService.uploadFavicon(file),
    onSuccess: (data) => {
      setBranding(data);
      queryClient.invalidateQueries({ queryKey: ['branding', 'me'] });
      toast.success('Favicon atualizado!');
    },
    onError: () => toast.error('Erro ao enviar favicon. Use PNG ou ICO (máx 512KB).'),
  });

  const handleSave = () => {
    const payload: UpdateBrandingDto = {};
    if (form.appName !== undefined) payload.appName = form.appName;
    if (form.customDomain !== undefined) payload.customDomain = form.customDomain;
    if (form.primaryColor !== undefined) payload.primaryColor = form.primaryColor;
    updateMutation.mutate(payload);
  };

  if (isLoading) return <div className="p-6 text-muted-foreground">Carregando...</div>;

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">White Label / Branding</h1>
        <p className="text-muted-foreground mt-1">
          Personalize a identidade visual da sua plataforma.
        </p>
      </div>

      {/* Logo e Favicon */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" /> Imagens
          </CardTitle>
          <CardDescription>Logo exibido na tela de login e no cabeçalho.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            {branding?.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt="Logo atual"
                className="h-16 w-16 rounded-lg object-contain border"
              />
            ) : (
              <div className="h-16 w-16 rounded-lg border flex items-center justify-center text-muted-foreground text-xs">
                Sem logo
              </div>
            )}
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoMutation.isPending}
              >
                {logoMutation.isPending ? 'Enviando...' : 'Alterar logo'}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG ou SVG · máx 2MB</p>
              <input
                ref={logoInputRef}
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) logoMutation.mutate(file);
                  e.target.value = '';
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            {branding?.faviconUrl ? (
              <img
                src={branding.faviconUrl}
                alt="Favicon atual"
                className="h-8 w-8 rounded object-contain border"
              />
            ) : (
              <div className="h-8 w-8 rounded border flex items-center justify-center text-muted-foreground text-xs">
                —
              </div>
            )}
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => faviconInputRef.current?.click()}
                disabled={faviconMutation.isPending}
              >
                {faviconMutation.isPending ? 'Enviando...' : 'Alterar favicon'}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">PNG ou ICO · máx 512KB</p>
              <input
                ref={faviconInputRef}
                type="file"
                className="hidden"
                accept="image/png,image/x-icon"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) faviconMutation.mutate(file);
                  e.target.value = '';
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Nome e cor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5" /> Identidade
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label>Nome do app</Label>
            <Input
              placeholder={branding?.appName ?? 'Ex: Minha Empresa'}
              defaultValue={branding?.appName ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, appName: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <Label className="flex items-center gap-2">
              <Palette className="h-4 w-4" /> Cor primária
            </Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                defaultValue={branding?.primaryColor ?? '#6366f1'}
                className="h-9 w-14 cursor-pointer rounded border p-1"
                onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))}
              />
              <Input
                placeholder="#6366f1"
                value={form.primaryColor ?? branding?.primaryColor ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))}
                className="w-32 font-mono"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Domínio customizado */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" /> Domínio customizado
          </CardTitle>
          <CardDescription>
            Configure um subdomínio ou domínio próprio. Aponte seu DNS para os servidores da
            plataforma e insira o domínio aqui.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>Domínio</Label>
          <Input
            placeholder={branding?.customDomain ?? 'app.suaempresa.com.br'}
            defaultValue={branding?.customDomain ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, customDomain: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground">
            Após configurar o DNS, o branding será aplicado automaticamente ao acessar por este
            domínio.
          </p>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={updateMutation.isPending} className="w-full">
        {updateMutation.isPending ? 'Salvando...' : 'Salvar configurações'}
      </Button>
    </div>
  );
}
